// Matrix background service - runs continuously after login
import { MatrixClientService } from '../../services/matrixClientService';
import { useMatrixStore } from '../../state/matrixStore';
import { useMessagingStore } from '../../state/messagingStore';
import { idbPruneOlderThan } from '../../services/avatarIdb';


class MatrixUserService {
  constructor() {
    this.client = null;
    this.service = null;
    this.isRunning = false;
    this.userId = null;
    this.mxid = null;
    this._unloadRegistered = false;
    this._restartTimer = null;
    this._restartAttempts = 0;
    this._networkListenerRegistered = false;
    this._dataPreloaded = false;
  }

async startForUser(u_id) {
  if (this.isRunning) {
    console.log('[MatrixService] Restart requested; stopping previous client for user:', this.userId);
    await this.stop(); // ensure a clean slate before starting again
  }

  try {
    console.log('[MatrixService] Starting for user:', u_id);
    this.userId = u_id;

    // 1. Get Matrix credentials
    const res = await fetch('/api/matrix/credentials', {
      credentials: 'include'
    });
    if (!res.ok) throw new Error(`Credentials failed: ${res.status}`);

    const { baseUrl, mxid, accessToken, deviceId } = await res.json();
    this.mxid = mxid;

    // Stash for debugging/telemetry
    try {
      localStorage.setItem('matrix_access_token', accessToken || '');
      localStorage.setItem('matrix_user_id', mxid || '');
    } catch {}

    // Hydrate durable read-state before we compute any "NEW" UI
    try {
      useMatrixStore.getState().hydrateReadStateForMxid(mxid);
    } catch {}

    // 2. Initialize Matrix client
    this.service = new MatrixClientService();
    await this.service.initialize(mxid, accessToken, baseUrl);
    this.client = this.service.client;

    // 3. Set up event listeners BEFORE starting client
    this.setupEventListeners();

    // 4. Start client
    await this.service.startClient({
      initialSyncLimit: 20,
      pollTimeout: 30000,
    });

    // Avatar-cache cleanup (30-day retention)
    try { idbPruneOlderThan(30 * 24 * 60 * 60 * 1000); } catch {}

    this.isRunning = true;

    // Register beforeunload handler once
    if (!this._unloadRegistered) {
      this._unloadRegistered = true;
      window.addEventListener('beforeunload', () => {
        try { this.client?.stopClient(); } catch {}
      });
    }

    // Register network online/offline listeners once
    if (!this._networkListenerRegistered) {
      this._networkListenerRegistered = true;
      window.addEventListener('online', () => {
        if (this.client && this.isRunning) {
          try { this.client.retryImmediately(); } catch {}
        } else if (this.userId) {
          this._scheduleRestart();
        }
      });
      window.addEventListener('offline', () => {
        useMatrixStore.getState().setConnectionState('reconnecting');
      });
    }

    // 5. Initial data preload
    this.preloadInitialData();

    console.log('[MatrixService] Started for user:', u_id, '| rooms:', this.client?.getRooms()?.length || 0);

    } catch (error) {
      console.error('[MatrixService] Failed to start:', error);
      useMatrixStore.getState().setConnectionState('error');
      throw error;
    }
  }

    setupEventListeners() {
    if (!this.client) return;

    this.client.on('Room', this.handleNewRoom);
    this.client.on('RoomState.events', this.handleRoomState);
    this.client.on('sync', this.handleSync);
    this.client.on('RoomMember.typing', this.handleTyping);
    this.client.on('User.presence', this.handlePresence);
    this.client.on('Room.receipt', this.handleReceipt);
    }

handleNewRoom = (room) => {
  const my = room.getMyMembership?.() || '';

  // If I have left/banned this room, ensure it is removed from the store.
  if (my === 'leave' || my === 'ban') {
    useMatrixStore.getState().removeRoom(room.roomId);
    return;
  }

  const roomData = this.mapRoomToStore(room);
  useMatrixStore.getState().upsertRoom(roomData);
};


    handleRoomState = (event, state, prevState) => {
    // This handles room state changes (membership, room name, etc.)
    console.log('[MatrixService] Room state changed:', event?.getType(), 'in room:', state?.roomId);

    // Handle com.ehandshake.invite.accepted custom state event (absorbed from useMatrixClient)
    if (event?.getType() === 'com.ehandshake.invite.accepted') {
      const roomId = event.getRoomId();
      const content = event.getContent ? event.getContent() : {};
      console.log('[MatrixService] ACCEPT EVENT RECEIVED:', { roomId, content });
      try {
        useMatrixStore.getState().handleInviteAccepted(roomId, content);
      } catch (err) {
        console.error('[MatrixService] Error in handleInviteAccepted:', err);
      }
    }

    // Update the room in the store when state changes
    if (state && state.roomId) {
      const room = this.client.getRoom(state.roomId);
      if (room) {
        const my = room.getMyMembership?.() || '';
        if (my === 'leave' || my === 'ban') {
          useMatrixStore.getState().removeRoom(room.roomId);
          console.log('[MatrixService] Evicted left/banned room on state change:', room.roomId);
          return;
        }
        const roomData = this.mapRoomToStore(room);
        useMatrixStore.getState().upsertRoom(roomData);
      }
    }

    }

  handleSync = (state, prevState, data) => {
    console.log('[MatrixService] sync state:', state, '(prev:', prevState + ')');

    // Map SDK sync states → store connection states
    const map = {
      'PREPARED':     'connected',
      'SYNCING':      'connected',      // healthy incremental sync
      'RECONNECTING': 'reconnecting',   // SDK is retrying automatically
      'ERROR':        'error',           // SDK gave up or hit fatal error
      'STOPPED':      'disconnected',    // sync loop fully stopped
    };
    const connectionState = map[state] || 'syncing';
    useMatrixStore.getState().setConnectionState(connectionState);

    // Reset restart attempts on healthy sync
    if (state === 'PREPARED' || state === 'SYNCING') {
      this._restartAttempts = 0;
    }

    if (state === 'PREPARED' || (state === 'SYNCING' && !this._dataPreloaded)) {
      this._dataPreloaded = true;
      console.log('[MatrixService] Initial sync complete');
      this.preloadInitialData();
      this.preloadAllRoomData();

      // Broadcast own presence as online so other users receive presence events
      try { this.client.setPresence({ presence: 'online' }); } catch (e) {
        console.warn('[MatrixService] Failed to set presence:', e);
      }
    }

    // Auto-restart on fatal stop (e.g. token expired, unrecoverable error)
    if (state === 'ERROR' || state === 'STOPPED') {
      console.warn('[MatrixService] Sync entered', state, '— scheduling restart');
      this._scheduleRestart();
    }
  }

  _scheduleRestart() {
    if (this._restartTimer) return; // already scheduled
    this._restartAttempts = (this._restartAttempts || 0) + 1;
    if (this._restartAttempts > 5) {
      console.error('[MatrixService] Max restart attempts (5) reached. Manual reload required.');
      return;
    }
    const delay = Math.min(5000 * Math.pow(2, this._restartAttempts - 1), 60000);
    console.log(`[MatrixService] Restart attempt ${this._restartAttempts} in ${delay}ms`);
    this._restartTimer = setTimeout(async () => {
      this._restartTimer = null;
      try {
        await this.startForUser(this.userId);
        this._restartAttempts = 0; // reset on success
      } catch (e) {
        console.error('[MatrixService] Restart failed:', e);
      }
    }, delay);
  }

  handleTyping = (event, member) => {
    // member.roomId is the reliable source — event.getRoomId() returns undefined
    // for ephemeral events because the SDK mapper doesn't set room_id on them.
    // member.typing is a boolean set by RoomMember.setTypingEvent() after checking
    // the user_ids array in the m.typing event content.
    const roomId = member.roomId;
    const userId = member.userId;
    const isTyping = member.typing;

    if (!roomId || !userId) return;
    useMatrixStore.getState().setTypingUser(roomId, userId, isTyping);
  }

  handlePresence = (event, user) => {
    // User.presence fires with (event, User) — user.presence holds the state string.
    // The event name is 'User.presence' (NOT 'RoomMember.presence' which doesn't exist).
    const userId = user.userId;
    const presence = user.presence || event?.getContent?.()?.presence || 'offline';

    if (!userId) return;
    useMatrixStore.getState().setUserPresence(userId, presence);
  }

  handleReceipt = (event, room) => {
    // Cross-device read receipts are processed by matrixClientService's Room.receipt handler.
    // This handler is for any future receipt-related logic at the service layer.
  }

  /**
   * Send a read receipt for the currently viewed room (debounced externally).
   */
  sendReadReceipt(roomId) {
    if (!this.service || !roomId) return;
    this.service.sendReadReceipt(roomId);
  }

  /**
   * Send an emoji reaction to a message.
   */
  async sendReaction(roomId, targetEventId, emoji) {
    if (!this.service || !roomId || !targetEventId || !emoji) return;
    return this.service.sendReaction(roomId, targetEventId, emoji);
  }

  /**
   * Send a reply to a specific message.
   */
  async sendReply(roomId, replyToEventId, replyToSender, replyToBody, message) {
    if (!this.service || !roomId || !replyToEventId || !message) return;
    return this.service.sendReply(roomId, replyToEventId, replyToSender, replyToBody, message);
  }

  /**
   * Remove a reaction by redacting its event.
   */
  async removeReaction(roomId, reactionEventId) {
    if (!this.service || !roomId || !reactionEventId) return;
    return this.service.removeReaction(roomId, reactionEventId);
  }

  /**
   * Edit an existing message (Matrix m.replace).
   */
  async editMessage(roomId, originalEventId, newBody) {
    if (!this.service || !roomId || !originalEventId || !newBody) return;
    return this.service.editMessage(roomId, originalEventId, newBody);
  }

  /**
   * Delete (redact) a message.
   */
  async deleteMessage(roomId, eventId) {
    if (!this.service || !roomId || !eventId) return;
    return this.service.deleteMessage(roomId, eventId);
  }

    mapRoomToStore(room) {
      // Always get myUserId FIRST
      const myUserId = this.client?.getUserId?.() || '';

      // Members & peer
      const members = room.getMembers();
      const peerMember = members.find(m => m.userId && m.userId !== myUserId);

      // Fix avatar URL
      let avatarUrl = null;
      try {
        const mxcUrl = room.getAvatarUrl();
        if (mxcUrl && this.client) {
          avatarUrl = this.client.mxcUrlToHttp(mxcUrl, 28, 28, 'crop');
        }
      } catch (e) {
        console.warn('[MatrixService] Failed to get room avatar:', e);
      }

      // Compute inviter/invitee state
      const human = (m) => /^@u\d+:/i.test(m?.userId);
      const joined  = members.filter(m => String(m?.membership || '').toLowerCase() === 'join'   && human(m));
      const invited = members.filter(m => String(m?.membership || '').toLowerCase() === 'invite' && human(m));
      const inviterPending = myUserId && joined.some(m => m.userId === myUserId) && invited.length === 1;

      return {
        roomId: room.roomId,
        alias: room.getCanonicalAlias(),
        name: room.name || this.getRoomDisplayName(room),
        avatarUrl,

        lastMessage: this.getLastMessage(room),
        lastMessageTs: this.getLastMessageTs(room),
        unreadCount: room.getUnreadNotificationCount(),

        peerUserId: peerMember ? this.extractUid(peerMember.userId) : null,

        membership: room.getMyMembership(),      // <- source of truth
        isDirect: this.isRoomDirectMessage(room) || false,
        inviterPending,
        joinedCount: joined.length,
        invitedCount: invited.length,

        lastUpdated: Date.now()
      };
    }


    getRoomDisplayName(room) {
    try {
        const myUserId = this.client.getUserId();
        const members = room.getJoinedMembers();
        const otherMembers = members.filter(m => m.userId !== myUserId);
        
        if (otherMembers.length === 1) {
        return otherMembers[0].name || otherMembers[0].userId;
        } else if (otherMembers.length > 1) {
        return otherMembers.map(m => m.name || m.userId).join(', ');
        }
        
        return room.getCanonicalAlias() || room.roomId;
    } catch (e) {
        return room.roomId;
    }
    }

isRoomDirectMessage(room) {
  try {
    // 0) If my m.direct contains this roomId, it's a DM
    try {
      const dmContent = this.client.getAccountData('m.direct')?.getContent() || {};
      for (const ids of Object.values(dmContent)) {
        if (Array.isArray(ids) && ids.includes(room.roomId)) return true;
      }
    } catch (_) {}

    // 1) Creation flag
    const createEvent = room.currentState?.getStateEvents?.('m.room.create', '');
    const isDirectFromCreate = createEvent?.getContent?.()?.is_direct === true;

    // 2) Canonical alias pattern
    const alias = room.getCanonicalAlias?.() || '';
    const isDMAlias = !!(alias && /^#eh\.dm\.u\d+_u\d+:/i.test(alias));

    // 3) Membership heuristic: me JOIN + one other INVITE
    let meJoinPlusOneInvite = false;
    try {
      const myUserId = this.client?.getUserId?.() || '';
      const members = room.getMembers?.() || [];
      const joined = members.filter(m => String(m?.membership || '').toLowerCase() === 'join');
      const invited = members.filter(m => String(m?.membership || '').toLowerCase() === 'invite');
      const joinedHuman = joined.filter(m => /^@u\d+:/i.test(m?.userId)).map(m => m.userId);
      const invitedHuman = invited.filter(m => /^@u\d+:/i.test(m?.userId)).map(m => m.userId);
      meJoinPlusOneInvite = joinedHuman.includes(myUserId) && invitedHuman.length === 1;
    } catch (_) {}

    const twoJoined = (() => {
      try {
        const j = room.getJoinedMembers?.() || [];
        return j.filter(m => /^@u\d+:/i.test(m?.userId)).length === 2;
      } catch { return false; }
    })();

    return !!(isDirectFromCreate || isDMAlias || meJoinPlusOneInvite || twoJoined);
  } catch (e) {
    console.warn('[MatrixService] Error checking if room is direct:', e);
    return false;
  }
}

  getLastMessage(room) {
    const events = room.getLiveTimeline().getEvents();
    for (let i = events.length - 1; i >= 0; i--) {
      const event = events[i];
      if (event.getType() === 'm.room.message') {
        let body = event.getContent().body || 'Message';
        // Strip Matrix reply fallback
        if (typeof body === 'string' && body.startsWith('> ')) {
          const idx = body.indexOf('\n\n');
          if (idx >= 0) body = body.slice(idx + 2);
        }
        return body;
      }
    }
    return 'No messages';
  }

    getLastMessageTs(room) {
    try {
        const events = room.getLiveTimeline().getEvents();
        for (let i = events.length - 1; i >= 0; i--) {
        const event = events[i];
        if (event.getType() === 'm.room.message') {
            const ts = event.getTs();
            // Validate timestamp is reasonable (not in distant future/past)
            if (ts > 1000000000000 && ts < Date.now() + 86400000) { // Between 2001 and tomorrow
            return ts;
            }
        }
        }
        
        // Use room creation time as fallback
        const createEvent = room.currentState.getStateEvents('m.room.create', '');
        if (createEvent) {
        const createTs = createEvent.getTs();
        if (createTs > 1000000000000 && createTs < Date.now() + 86400000) {
            return createTs;
        }
        }
        
        // Final fallback: current time minus a small offset to maintain order
        const safeFallback = Date.now() - Math.floor(Math.random() * 1000000);
        return safeFallback;
        
    } catch (error) {
        console.warn(`[MatrixService] Error getting timestamp for ${room.roomId}, using current time:`, error);
        return Date.now();
    }
    }

  extractUid(mxid) {
    const match = mxid.match(/^@u(\d+):/);
    return match ? Number(match[1]) : null;
  }

  mapEventToMessage(event) {
    try {
      const content = event.getContent();
      const sender = event.getSender();

      let bodyText = content?.body || '';

      // Extract reply-to info — try multiple sources (SDK may strip m.relates_to)
      let replyToEventId = null;
      try {
        replyToEventId = content?.['m.relates_to']?.['m.in_reply_to']?.event_id || null;
        if (!replyToEventId && event.event?.content?.['m.relates_to']?.['m.in_reply_to']?.event_id) {
          replyToEventId = event.event.content['m.relates_to']['m.in_reply_to'].event_id;
        }
        if (!replyToEventId && typeof event.getWireContent === 'function') {
          const wire = event.getWireContent() || {};
          replyToEventId = wire?.['m.relates_to']?.['m.in_reply_to']?.event_id || null;
        }
      } catch (e) { /* defensive */ }

      // Strip Matrix reply fallback from body (always strip if pattern matches)
      if (typeof bodyText === 'string' && bodyText.startsWith('> ')) {
        const idx = bodyText.indexOf('\n\n');
        if (idx >= 0) {
          bodyText = bodyText.slice(idx + 2);
        }
      }

      return {
        eventId: event.getId(),
        roomId: event.getRoomId(),
        sender: sender,
        senderUid: this.extractUid(sender),

        // Text payload – ChatView expects `body`
        body: bodyText,

        // Keep `content` for any other callers that were using it
        content: bodyText,

        msgtype: content.msgtype || 'm.text',
        timestamp: event.getTs(),
        type: event.getType(),
        unsigned: event.getUnsigned() || {},
        redacted: event.isRedacted() || false,
        replyToEventId,
      };
    } catch (error) {
      console.error('[MatrixService] Error mapping event to message:', error);
      return {
        eventId: event.getId() || `err-${Date.now()}`,
        roomId: event.getRoomId() || 'unknown',
        sender: 'unknown',
        body: 'Error loading message',
        content: 'Error loading message',
        msgtype: 'm.text',
        timestamp: Date.now(),
        type: 'm.room.message',
        error: true,
      };
    }
  }

  reconcileOptimisticRoom(room) {
    const alias = room.getCanonicalAlias();
    if (alias) {
      useMessagingStore.getState().reconcileDM({ 
        alias, 
        roomId: room.roomId 
      });
    }
  }

preloadInitialData() {
  const rooms = this.client.getRooms();
  console.log(`[MatrixService] Preloading ${rooms.length} rooms`);
  let totalMessages = 0;
  const emptyRooms = [];

  rooms.forEach(room => {
    const roomData = this.mapRoomToStore(room);
    useMatrixStore.getState().upsertRoom(roomData);

    // Hydrate messages from SDK cache into Zustand store
    try {
      const events = room.getLiveTimeline().getEvents();
      const messages = events
        .filter(e => e.getType() === 'm.room.message')
        .map(e => this.mapEventToMessage(e));
      if (messages.length) {
        useMatrixStore.getState().setMessages(room.roomId, messages);
        totalMessages += messages.length;
      } else {
        emptyRooms.push(room);
      }
    } catch (e) {
      console.warn('[MatrixService] Failed to hydrate messages for', room.roomId, e);
    }
  });

  // Initialize unreadCounts from durable read state:
  // If the user has already read past a room's last message, suppress the unread count.
  try {
    const store = useMatrixStore.getState();
    const lastReadMap = store.lastReadTsByRoom || {};
    rooms.forEach(room => {
      const roomId = room.roomId;
      const sdkUnread = room.getUnreadNotificationCount?.() || 0;
      if (sdkUnread <= 0) return;
      const lastReadTs = lastReadMap[roomId] || 0;
      const roomData = store.rooms[roomId];
      const lastMsgTs = roomData?.lastMessageTs || 0;
      if (lastReadTs && lastReadTs >= lastMsgTs) {
        // User already read this room — suppress unread
        store.clearUnreadCount(roomId);
      } else {
        // Genuinely unread messages — seed the count from the SDK
        store.setUnreadCount(roomId, sdkUnread);
      }
    });
  } catch (e) {
    console.warn('[MatrixService] Failed to initialize unread counts from read state:', e);
  }

  console.log(`[MatrixService] Preloaded ${rooms.length} rooms, ${totalMessages} cached messages, ${emptyRooms.length} need scrollback`);

  // Sweep stale optimistic entries that now have a real room
  try {
    const realAliases = new Set();
    rooms.forEach(room => {
      const alias = room.getCanonicalAlias();
      if (alias) realAliases.add(alias);
    });
    const ms = useMessagingStore.getState();
    const stale = (ms.optimisticThreads || []).filter(t =>
      realAliases.has(t.alias) || (t.createdAt && Date.now() - t.createdAt > 30000)
    );
    stale.forEach(t => ms.reconcileDM({ alias: t.alias }));
    if (stale.length) console.log(`[MatrixService] Swept ${stale.length} stale optimistic entries`);
  } catch (e) {
    console.warn('[MatrixService] Optimistic sweep error:', e);
  }

  // For rooms with no cached timeline events (e.g. after page refresh),
  // fetch historical messages from the server via scrollback
  if (emptyRooms.length) {
    this._scrollbackEmptyRooms(emptyRooms);
  }
}

async _scrollbackEmptyRooms(rooms) {
  console.log(`[MatrixService] Fetching messages for ${rooms.length} rooms via scrollback`);
  let filled = 0;
  const promises = rooms.map(async (room) => {
    try {
      await this.client.scrollback(room, 20);
      const events = room.getLiveTimeline().getEvents();
      const messages = events
        .filter(e => e.getType() === 'm.room.message')
        .map(e => this.mapEventToMessage(e));
      if (messages.length) {
        useMatrixStore.getState().setMessages(room.roomId, messages);
        filled++;
      }
    } catch (e) {
      console.warn('[MatrixService] Scrollback failed for', room.roomId, e);
    }
  });
  await Promise.allSettled(promises);
  console.log(`[MatrixService] Scrollback complete: ${filled}/${rooms.length} rooms filled`);
}

  /**
   * Load older messages for a room via scrollback (for pagination).
   * Returns the count of messages now in the store for this room.
   */
  async loadOlderMessages(roomId, limit = 30) {
    if (!this.client || !roomId) return 0;

    const store = useMatrixStore.getState();

    // Guard against concurrent requests for the same room
    const ps = store.paginationState[roomId];
    if (ps?.isLoading) return 0;

    store.setPaginationLoading(roomId, true);

    try {
      const room = this.client.getRoom(roomId);
      if (!room) {
        store.setPaginationLoading(roomId, false);
        return 0;
      }

      await this.client.scrollback(room, limit);

      // After scrollback the room's live timeline contains old + new events
      const events = room.getLiveTimeline().getEvents();
      const messages = events
        .filter(e => e.getType() === 'm.room.message')
        .map(e => this.mapEventToMessage(e));

      store.setMessages(roomId, messages);

      // Check if more history exists
      const timeline = room.getLiveTimeline();
      const token = timeline.getPaginationToken?.('b') ?? timeline.getPaginationToken?.('backward') ?? null;
      store.setPaginationHasMore(roomId, !!token);

      return messages.length;
    } catch (e) {
      console.warn('[MatrixService] loadOlderMessages failed for', roomId, e);
      return 0;
    } finally {
      store.setPaginationLoading(roomId, false);
    }
  }

  preloadAllRoomData() {
    // After sync complete, preload profiles for all peers
    const rooms = this.client.getRooms();
    const peerUids = new Set();
    
    rooms.forEach(room => {
      const members = room.getMembers();
      const myUserId = this.client.getUserId();
      
      members.forEach(member => {
        if (member.userId !== myUserId) {
          const uid = this.extractUid(member.userId);
          if (uid) peerUids.add(uid);
        }
      });
    });
    
    // Preload all peer profiles
    this.preloadPeerProfiles(Array.from(peerUids));
  }

  async preloadPeerProfiles(uids) {
    if (!uids.length) return;

    console.log(`[MatrixService] Preloading ${uids.length} peer profiles (batch)`);

    try {
      // Single batch request instead of N individual fetches
      const res = await fetch(`/api/teams/users/by-ids?ids=${uids.join(',')}`, { credentials: 'include' });
      if (!res.ok) {
        console.warn('[MatrixService] Batch profile fetch failed:', res.status);
        return;
      }
      const data = await res.json();
      const users = data.users || [];
      users.forEach(u => {
        const uid = u.u_id;
        const name = `${u.u_first_name || ''} ${u.u_last_name || ''}`.trim() || `User ${uid}`;
        const avatarUrl = u.u_profile_picture || null;
        useMatrixStore.getState().setUserProfile(uid, { name, avatarUrl });
      });
      console.log(`[MatrixService] Peer profiles preloaded: ${users.length} users in 1 request`);
    } catch (e) {
      console.warn('[MatrixService] Batch profile fetch error:', e);
    }
  }

  /**
   * Delete a single conversation for the current user:
   * - leave the room
   * - forget the room
   */
  async deleteConversation(roomId) {
    if (!this.client || !roomId) {
      console.warn('[MatrixService][DELETE] aborted: missing client/roomId', { hasClient: !!this.client, roomId });
      return;
    }

    // ==== SNAPSHOT (pre) ======================================================
    try {
      const st = useMatrixStore.getState();
      const preRooms = Object.keys(st.rooms || {});
      console.log('[MatrixService][DELETE] start', {
        roomId,
        selectedRoomId: st.selectedRoomId,
        roomsCount: preRooms.length,
        first10: preRooms.slice(0, 10)
      });
    } catch (e) { console.warn('[MatrixService][DELETE] pre snapshot failed', e); }

    // ==== SERVER: leave & forget =============================================
    try {
      await this.client.leave(roomId);
      const memAfterLeave = this.client.getRoom(roomId)?.getMyMembership?.();
      console.log('[MatrixService][DELETE] leave() done', roomId, 'myMembership:', memAfterLeave);
    } catch (e) {
      console.warn('[MatrixService][DELETE] leave failed', roomId, e);
    }
    try {
      await this.client.forget(roomId);
      const stillThere = !!this.client.getRoom(roomId);
      console.log('[MatrixService][DELETE] forget() done', roomId, 'client.getRoom exists?', stillThere);
    } catch (e) {
      console.warn('[MatrixService][DELETE] forget failed', roomId, e);
    }

    // ==== CACHE: drop SDK cache entry if supported ============================
    try {
      if (this.client.store?.deleteRoom) {
        await this.client.store.deleteRoom(roomId);
        console.log('[MatrixService][DELETE] store.deleteRoom() OK', roomId);
      }
    } catch (e) {
      console.warn('[MatrixService][DELETE] store.deleteRoom failed', roomId, e);
    }

    // ==== STORE: HARD EVICT & FORCE REPAINT ===================================
    try {
      useMatrixStore.setState(s => {
        const nextRooms = { ...(s.rooms || {}) };
        if (nextRooms[roomId]) delete nextRooms[roomId];

        const nextMessages = { ...(s.messages || {}) };
        if (nextMessages[roomId]) delete nextMessages[roomId];

        const nextUnread = s.unreadCounts ? { ...s.unreadCounts } : undefined;
        if (nextUnread && nextUnread[roomId] != null) delete nextUnread[roomId];

        const nextTyping = s.typing ? { ...s.typing } : undefined;
        if (nextTyping && nextTyping[roomId]) delete nextTyping[roomId];

        const clearedSelection = s.selectedRoomId === roomId ? null : s.selectedRoomId;

        // bump a version tick so any selectors subscribed to shallow equality will refire
        const nextVersion = (s.roomsVersion || 0) + 1;

        return {
          rooms: nextRooms,
          messages: nextMessages,
          unreadCounts: nextUnread ?? s.unreadCounts,
          typing: nextTyping ?? s.typing,
          selectedRoomId: clearedSelection,
          roomsVersion: nextVersion
        };
      });

      const st2 = useMatrixStore.getState();
      const postRooms = Object.keys(st2.rooms || {});
      console.log('[MatrixService][DELETE] evicted from store', {
        roomId,
        roomsCount: postRooms.length,
        first10: postRooms.slice(0, 10),
        selectedRoomId: st2.selectedRoomId,
        roomsVersion: st2.roomsVersion
      });
    } catch (e) {
      console.warn('[MatrixService][DELETE] hard-evict failed', e);
    }
    // 5) Backend: remove mirror row from matrix_conversations
    try {
      const res = await fetch(`/api/matrix/conversations/${encodeURIComponent(roomId)}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      console.log('[MatrixService][DELETE] backend mirror removed', data);
    } catch (e) {
      console.warn('[MatrixService][DELETE] backend mirror delete failed', e);
    }
  }



  async stop() {
    // Cancel any pending restart
    if (this._restartTimer) {
      clearTimeout(this._restartTimer);
      this._restartTimer = null;
    }

    if (this.client) {
      try { this.client.removeAllListeners?.(); } catch (_) {}
      try { await this.client.stopClient(); } catch (e) {
        console.warn('[MatrixService] stopClient error (ignored):', e);
      }
    }
    this.isRunning = false;
    this.client = null;
    this.service = null;
    this.userId = null;
    this.mxid = null;
    this._dataPreloaded = false;

    useMatrixStore.getState().setConnectionState('disconnected');
    console.log('[MatrixService] Stopped');
  }

}


// Singleton instance
export const matrixUserService = new MatrixUserService();

// Convenience function for login.js to call
export const startUserMatrixService = (u_id) => {
  return matrixUserService.startForUser(u_id);
};